import { useMemo, useState } from "react";
import { LogOut, Save, ShieldAlert, ShieldCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useT } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { useData } from "@/context/DataContext";
import { Badge, Button, Card, SectionHeading } from "@/components/ui";
import { LanguageSwitcher } from "@/components/layout/LanguageSwitcher";
import { initialsOf } from "@/lib/format";
import { composeDisplayName } from "@/lib/person-name";
import { PersonNameFields } from "@/components/PersonNameFields";
import { departments, municipalitiesOf, canonicalLocation } from "@/lib/locations";

export function Profile() {
  const { t } = useT();
  const auth = useAuth();
  const { data, updateProfile } = useData();
  const [form, setForm] = useState(() => ({
    ...data.profile,
    ...canonicalLocation(data.profile.department, data.profile.city),
  }));
  const [notifs, setNotifs] = useState({ email: true, expiry: true, marketing: false });

  const phone = auth.status === "authenticated" ? auth.user.phone : "";
  const role = auth.status === "authenticated" ? auth.user.role : "user";
  const fullName =
    composeDisplayName(form) || (form.companyName ?? "");
  const cityOptions = useMemo(() => municipalitiesOf(form.department), [form.department]);

  const save = () => {
    updateProfile(form);
    toast.success(t("common.save"));
  };

  return (
    <div className="space-y-8">
      <SectionHeading title={t("profile.title")} subtitle={t("profile.subtitle")} />

      <Card className="flex flex-col items-start gap-5 p-6 sm:flex-row sm:items-center">
        <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-brand-500 to-brand-700 text-xl font-semibold text-white">
          {fullName ? initialsOf(fullName) : phone.slice(-2)}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xl font-semibold text-slate-900">{fullName || `+${phone}`}</p>
          <p className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-500">
            <span>+{phone}</span>
            {role === "admin" ? (
              <Badge tone="amber" dot>
                <ShieldCheck className="h-3 w-3" />
                {t("auth.role_admin")}
              </Badge>
            ) : (
              <Badge tone="brand" dot>
                {t("auth.role_user")}
              </Badge>
            )}
          </p>
        </div>
        <Button variant="secondary" onClick={() => auth.signOut()}>
          <LogOut className="h-4 w-4" />
          {t("common.logout")}
        </Button>
      </Card>

      <Card className="p-6">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500">{t("profile.personal")}</h3>
        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          {form.kind === "juridica" ? (
            <label className="block sm:col-span-2">
              <span className="label">{t("name.company")}</span>
              <input
                className="input mt-1.5"
                value={form.companyName ?? ""}
                onChange={(e) => setForm({ ...form, companyName: e.target.value })}
              />
            </label>
          ) : (
            <div className="sm:col-span-2">
              <PersonNameFields
                value={{
                  firstName: form.firstName,
                  secondName: form.secondName,
                  firstSurname: form.firstSurname,
                  secondSurname: form.secondSurname,
                }}
                onChange={(next) => setForm({ ...form, ...next })}
              />
            </div>
          )}
          <label className="block">
            <span className="label">{t("auth.phone_label")}</span>
            <input
              className="input mt-1.5 bg-slate-100 text-slate-500"
              value={`+${phone}`}
              readOnly
              aria-readonly
            />
          </label>
          <label className="block">
            <span className="label">{t("req.field_email")}</span>
            <input
              className="input mt-1.5"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </label>
          <label className="block sm:col-span-2">
            <span className="label">{t("req.field_address")}</span>
            <input
              className="input mt-1.5"
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
            />
          </label>
          <label className="block">
            <span className="label">{t("req.field_department")}</span>
            <select
              className="input mt-1.5"
              value={form.department}
              onChange={(e) => setForm({ ...form, department: e.target.value, city: "" })}
            >
              <option value="">{t("req.select_department")}</option>
              {departments.map((d) => (
                <option key={d.code} value={d.name}>
                  {d.name}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="label">{t("req.field_city")}</span>
            <select
              className="input mt-1.5"
              value={form.city}
              disabled={!form.department}
              onChange={(e) => setForm({ ...form, city: e.target.value })}
            >
              <option value="">
                {form.department ? t("req.select_city") : t("req.select_city_first")}
              </option>
              {cityOptions.map((m) => (
                <option key={m.code} value={m.name}>
                  {m.name}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="label">{t("req.field_doc_type")}</span>
            <select
              className="input mt-1.5"
              value={form.documentType}
              onChange={(e) => setForm({ ...form, documentType: e.target.value as typeof form.documentType })}
            >
              <option value="CC">Cédula de ciudadanía</option>
              <option value="CE">Cédula de extranjería</option>
              <option value="PA">Pasaporte</option>
              <option value="NIT">NIT</option>
            </select>
          </label>
          <label className="block">
            <span className="label">{t("req.field_doc_number")}</span>
            <input
              className="input mt-1.5"
              value={form.documentNumber}
              onChange={(e) => setForm({ ...form, documentNumber: e.target.value })}
            />
          </label>
        </div>
        <div className="mt-6 flex justify-end">
          <Button onClick={save}>
            <Save className="h-4 w-4" />
            {t("common.save")}
          </Button>
        </div>
      </Card>

      <Card className="p-6">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500">
          {t("profile.notifications")}
        </h3>
        <div className="mt-5 space-y-4">
          <Toggle
            label={t("profile.notif_email")}
            checked={notifs.email}
            onChange={(v) => setNotifs({ ...notifs, email: v })}
          />
          <Toggle
            label={t("profile.notif_expiry")}
            checked={notifs.expiry}
            onChange={(v) => setNotifs({ ...notifs, expiry: v })}
          />
          <Toggle
            label={t("profile.notif_marketing")}
            checked={notifs.marketing}
            onChange={(v) => setNotifs({ ...notifs, marketing: v })}
          />
        </div>
      </Card>

      <Card className="p-6">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500">
          {t("profile.language")}
        </h3>
        <div className="mt-5">
          <LanguageSwitcher />
        </div>
      </Card>

      <Card className="border-red-200 bg-red-50/30 p-6">
        <div className="flex items-start gap-4">
          <span className="inline-flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-red-100 text-red-700">
            <ShieldAlert className="h-5 w-5" />
          </span>
          <div className="flex-1">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-red-700">
              {t("profile.danger")}
            </h3>
            <p className="mt-2 text-sm text-slate-600">
              Al eliminar tu cuenta perderás el acceso al portal, pero tus certificados emitidos
              seguirán siendo válidos. Escríbenos para revocar si lo necesitas.
            </p>
            <Button variant="danger" className="mt-4">
              <Trash2 className="h-4 w-4" />
              {t("profile.delete")}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-center justify-between gap-4">
      <span className="text-sm text-slate-700">{label}</span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={
          "relative inline-flex h-6 w-11 flex-none items-center rounded-full transition " +
          (checked ? "bg-brand-600" : "bg-slate-300")
        }
      >
        <span
          className={
            "inline-block h-5 w-5 transform rounded-full bg-white shadow transition " +
            (checked ? "translate-x-5" : "translate-x-0.5")
          }
        />
      </button>
    </label>
  );
}
